import React, { useState, useEffect } from 'react';
import C from '../../constants/colors';
import * as hseApi from '../../api/hse';
import { RealQRCode } from '../../utils/qr';

function formatValidity(months) {
  if (!months) return 'No expiry';
  if (months % 12 === 0) return `Valid ${months / 12} year${months / 12 !== 1 ? 's' : ''}`;
  return `Valid ${months} month${months !== 1 ? 's' : ''}`;
}

export default function HseCoursesView({ companyId, siteId, sites = [] }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkPassing, setBulkPassing] = useState(false);
  const [attendanceCourse, setAttendanceCourse] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [showProfilerQr, setShowProfilerQr] = useState(false);

  useEffect(() => {
    if (companyId) {
      setLoading(true);
      hseApi.listCourses(companyId, siteId).then(setCourses).finally(() => setLoading(false));
    }
  }, [companyId, siteId]);

  async function handleSave(data) {
    if (data.id) {
      const updated = await hseApi.updateCourse(data.id, data);
      setCourses(prev => prev.map(c => c.id === updated.id ? { ...updated, assignedCount: c.assignedCount } : c));
    } else {
      const created = await hseApi.createCourse({ ...data, companyId, siteId });
      setCourses(prev => [...prev, { ...created, assignedCount: 0 }]);
    }
    setModal(null);
  }

  async function handleToggleActive(course) {
    const updated = await hseApi.updateCourse(course.id, {
      name: course.name,
      description: course.description,
      url: course.url,
      validityMonths: course.validityMonths,
      isActive: !course.isActive,
    });
    setCourses(prev => prev.map(c => c.id === updated.id ? { ...updated, assignedCount: c.assignedCount } : c));
  }

  async function handlePromote(course) {
    const updated = await hseApi.updateCourse(course.id, {
      name: course.name,
      description: course.description,
      url: course.url,
      validityMonths: course.validityMonths,
      scope: 'ENTERPRISE',
    });
    setCourses(prev => prev.map(c => c.id === updated.id ? { ...updated, assignedCount: c.assignedCount } : c));
  }

  async function handleDelete(course) {
    if (!window.confirm(`Delete "${course.name}"? Site copies will become standalone site courses.`)) return;
    await hseApi.deleteCourse(course.id);
    setCourses(prev => prev.filter(c => c.id !== course.id));
  }

  async function loadAttendance(course) {
    setAttendanceCourse(course);
    setAttendanceLoading(true);
    try {
      const data = await hseApi.listAttendance(course.id);
      setAttendanceRecords(data);
    } finally {
      setAttendanceLoading(false);
    }
  }

  async function handleAddAttendance(data) {
    const created = await hseApi.addAttendance(attendanceCourse.id, data);
    setAttendanceRecords(prev => [created, ...prev]);
  }

  async function handleDeleteAttendance(recordId) {
    await hseApi.deleteAttendance(recordId);
    setAttendanceRecords(prev => prev.filter(r => r.id !== recordId));
  }

  async function loadRoster(course) {
    setRosterLoading(true);
    setSelected({});
    try {
      const data = await hseApi.getCourseRoster(course.id);
      setRoster(data);
    } finally {
      setRosterLoading(false);
    }
  }

  async function handleMarkPassed(row, personType) {
    const recordId = row.recordId;
    if (personType === 'EMPLOYEE') {
      await hseApi.updateTrainingRecord(row.personId, recordId, { status: 'PASS' });
    } else {
      await hseApi.updateVisitorTraining(row.personId, recordId, { status: 'PASS' });
    }
    setRoster(prev => {
      const key = personType === 'EMPLOYEE' ? 'employees' : 'visitors';
      return {
        ...prev,
        [key]: prev[key].map(r =>
          r.recordId === recordId ? { ...r, status: 'PASS', effectiveStatus: 'PASS' } : r
        ),
      };
    });
  }

  async function handleBulkPass() {
    const empIds = Object.entries(selected)
      .filter(([, v]) => v.personType === 'EMPLOYEE')
      .map(([, v]) => v.personId);
    const visIds = Object.entries(selected)
      .filter(([, v]) => v.personType === 'VISITOR')
      .map(([, v]) => v.personId);
    setBulkPassing(true);
    try {
      if (empIds.length > 0) {
        await hseApi.bulkPass({ employeeIds: empIds, courseId: roster.course.id, siteId: roster.course.siteId });
      }
      if (visIds.length > 0) {
        await hseApi.bulkPassVisitors({ visitorIds: visIds, courseId: roster.course.id, siteId: roster.course.siteId });
      }
      await loadRoster(roster.course);
      setSelected({});
    } finally {
      setBulkPassing(false);
    }
  }

  if (loading) return <div style={{ padding: '32px', color: C.textMuted }}>Loading…</div>;

  const activeCount = courses.filter(c => c.isActive).length;

  return (
    <div style={{ padding: '24px', maxWidth: '860px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: C.textPrimary, fontSize: '18px', fontWeight: 700, margin: 0 }}>HSE Training Courses</h2>
          <p style={{ color: C.textMuted, fontSize: '12px', margin: '4px 0 0' }}>{activeCount} active course{activeCount !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowProfilerQr(true)}
            style={{ background: 'transparent', color: C.blue, border: `1px solid ${C.blue}44`, borderRadius: '7px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
          >
            Profiler QR
          </button>
          {!siteId && (
            <button
              onClick={() => setModal({})}
              style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              + New Course
            </button>
          )}
        </div>
      </div>

      {!siteId ? (
        // Company mode: flat list of ENTERPRISE masters
        courses.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '13px', padding: '32px 0', textAlign: 'center' }}>
            No company-wide courses yet. Create the first one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {courses.map(c => (
              <CourseCard
                key={c.id}
                course={c}
                onAttendance={() => loadAttendance(c)}
                onRoster={() => loadRoster(c)}
                onEdit={() => setModal(c)}
                onDelete={() => handleDelete(c)}
              />
            ))}
          </div>
        )
      ) : (
        // Site mode: two sections
        (() => {
          const enforced = courses.filter(c => c.parentCourseId);
          const siteOwned = courses.filter(c => !c.parentCourseId);
          return (
            <>
              {enforced.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                    Enforced by Company
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {enforced.map(c => (
                      <CourseCard key={c.id} course={c} enforced onAttendance={() => loadAttendance(c)} onRoster={() => loadRoster(c)} />
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Site Courses
                </div>
                <button
                  onClick={() => setModal({})}
                  style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  + New Course
                </button>
              </div>

              {siteOwned.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>
                  No site-specific courses yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {siteOwned.map(c => (
                    <CourseCard
                      key={c.id}
                      course={c}
                      onAttendance={() => loadAttendance(c)}
                      onRoster={() => loadRoster(c)}
                      onEdit={() => setModal(c)}
                      onToggleActive={() => handleToggleActive(c)}
                      onPromote={c.scope === 'SITE' && c.isActive ? () => handlePromote(c) : undefined}
                      onDelete={() => handleDelete(c)}
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()
      )}

      {(roster || rosterLoading) && (
        <RosterPanel
          roster={roster}
          loading={rosterLoading}
          selected={selected}
          onSelect={(recordId, meta) => setSelected(prev =>
            prev[recordId] ? (({ [recordId]: _, ...rest }) => rest)(prev) : { ...prev, [recordId]: meta }
          )}
          onMarkPassed={handleMarkPassed}
          onBulkPass={handleBulkPass}
          bulkPassing={bulkPassing}
          onClose={() => { setRoster(null); setSelected({}); }}
        />
      )}

      {attendanceCourse !== null && (
        <AttendanceLogPanel
          course={attendanceCourse}
          records={attendanceRecords}
          loading={attendanceLoading}
          siteId={siteId}
          sites={sites}
          onAdd={handleAddAttendance}
          onDelete={handleDeleteAttendance}
          onClose={() => { setAttendanceCourse(null); setAttendanceRecords([]); }}
        />
      )}

      {modal !== null && (
        <CourseModal
          course={modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
          sites={sites}
          isCompanyLevel={!siteId}
        />
      )}

      {showProfilerQr && (
        <HseProfilerQrModal onClose={() => setShowProfilerQr(false)} />
      )}
    </div>
  );
}

function CourseCard({ course: c, enforced = false, onAttendance, onRoster, onEdit, onToggleActive, onPromote, onDelete }) {
  return (
    <div style={{ background: C.surfaceElevated, border: `1px solid ${enforced ? C.blue + '44' : C.border}`, borderRadius: '8px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', opacity: c.isActive ? 1 : 0.55 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px' }}>
          <span
            onClick={onAttendance}
            style={{ color: C.textPrimary, fontWeight: 700, fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            {c.name}
          </span>
          {enforced
            ? <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>Enforced</span>
            : c.scope === 'ENTERPRISE'
              ? <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>ENTERPRISE</span>
              : null
          }
        </div>
        {c.description && <div style={{ color: C.textSecondary, fontSize: '12px', marginBottom: '6px' }}>{c.description}</div>}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
            {formatValidity(c.validityMonths)}
          </span>
          <span style={{ fontSize: '10px', color: c.isActive ? C.green : C.textMuted, fontWeight: 600 }}>
            {c.isActive ? 'Active' : 'Inactive'}
          </span>
          {c.assignedCount > 0 && (
            <span style={{ fontSize: '10px', color: C.textMuted }}>{c.assignedCount} assigned</span>
          )}
          {c.url && (
            <a href={c.url} target="_blank" rel="noreferrer" style={{ color: C.blue, fontSize: '10px', textDecoration: 'underline' }}>Reference link</a>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', marginLeft: '12px', flexShrink: 0 }}>
        {onRoster && (
          <button onClick={onRoster} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px' }}>Roster</button>
        )}
        {!enforced && onEdit && (
          <button onClick={onEdit} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px' }}>Edit</button>
        )}
        {!enforced && onPromote && (
          <button onClick={onPromote} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.blue, borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px' }}>Promote</button>
        )}
        {!enforced && onToggleActive && (
          <button onClick={onToggleActive} style={{ background: C.surface, border: `1px solid ${C.border}`, color: c.isActive ? C.red : C.green, borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px' }}>
            {c.isActive ? 'Deactivate' : 'Activate'}
          </button>
        )}
        {!enforced && onDelete && (
          <button onClick={onDelete} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.red, borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>
        )}
      </div>
    </div>
  );
}

function CourseModal({ course, onSave, onClose, sites = [], isCompanyLevel = false }) {
  const [name, setName] = useState(course.name || '');
  const [description, setDescription] = useState(course.description || '');
  const [url, setUrl] = useState(course.url || '');
  const [validityMonths, setValidityMonths] = useState(course.validityMonths ?? '');
  const [autoAssignOnEntry, setAutoAssignOnEntry] = useState(course.autoAssignOnEntry || false);
  const [selectedSiteIds, setSelectedSiteIds] = useState(
    course.deployedSiteIds || sites.map(s => s.id)
  );
  const [saving, setSaving] = useState(false);

  function toggleSite(siteId) {
    setSelectedSiteIds(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        id: course.id,
        name: name.trim(),
        description: description.trim() || null,
        url: url.trim() || null,
        validityMonths: validityMonths ? parseInt(validityMonths) : null,
        autoAssignOnEntry,
        ...(isCompanyLevel && { siteIds: selectedSiteIds }),
      });
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={handleSubmit} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '22px', width: '420px' }}>
        <h3 style={{ color: C.textPrimary, margin: '0 0 16px', fontSize: '15px' }}>{course.id ? 'Edit Course' : 'New Course'}</h3>
        <label style={{ display: 'block', color: C.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Course Name *</label>
        <input value={name} onChange={e => setName(e.target.value)} style={{ ...inputStyle, marginBottom: '12px' }} required />
        <label style={{ display: 'block', color: C.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, marginBottom: '12px', resize: 'vertical' }} />
        <label style={{ display: 'block', color: C.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reference URL</label>
        <input value={url} onChange={e => setUrl(e.target.value)} type="url" style={{ ...inputStyle, marginBottom: '12px' }} />
        <label style={{ display: 'block', color: C.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Validity (months) — leave blank for no expiry</label>
        <input value={validityMonths} onChange={e => setValidityMonths(e.target.value)} type="number" min="1" max="120" style={{ ...inputStyle, marginBottom: '18px' }} />
        {isCompanyLevel && sites.length > 0 && (
          <>
            <label style={{ display: 'block', color: C.textMuted, fontSize: '11px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Deploy to Sites (default: all)
            </label>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px', marginBottom: '18px', maxHeight: '140px', overflowY: 'auto' }}>
              {sites.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', cursor: 'pointer', fontSize: '12px', color: C.textSecondary }}>
                  <input
                    type="checkbox"
                    checked={selectedSiteIds.includes(s.id)}
                    onChange={() => toggleSite(s.id)}
                    style={{ accentColor: C.blue }}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: C.surface, borderRadius: '6px', border: `1px solid ${C.border}`, marginBottom: '18px', cursor: 'pointer' }} onClick={() => setAutoAssignOnEntry(v => !v)}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Auto-assign on first entry</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Automatically assign this course when someone enters this site for the first time</div>
          </div>
          <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: autoAssignOnEntry ? '#4caf50' : C.border, position: 'relative', flexShrink: 0, marginLeft: '12px', transition: 'background 0.2s' }}>
            <div style={{ position: 'absolute', top: '2px', left: autoAssignOnEntry ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSecondary, borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RosterPanel({ roster, loading, selected, onSelect, onMarkPassed, onBulkPass, bulkPassing, onClose }) {
  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#cdd6f4' }}>Loading roster…</div>
    </div>
  );
  if (!roster) return null;

  const STATUS_COLOR = { PASS: '#4caf50', PENDING: '#f59e0b', EXPIRED: '#ef4444' };
  const selectedCount = Object.keys(selected).length;
  const allRows = [
    ...roster.employees.map(r => ({ ...r, personType: 'EMPLOYEE' })),
    ...roster.visitors.map(r => ({ ...r, personType: 'VISITOR' })),
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#131a2b', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#e2e8f0' }}>{roster.course.name} — Roster</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{allRows.length} assigned</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {selectedCount > 0 && (
              <button
                onClick={onBulkPass}
                disabled={bulkPassing}
                style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: '#4caf50', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: bulkPassing ? 0.6 : 1 }}
              >
                {bulkPassing ? '…' : `Mark ${selectedCount} Passed`}
              </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: '#64748b', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {allRows.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No one assigned to this course yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <th style={{ width: '36px', padding: '10px 12px' }}></th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Completed</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Expires</th>
                  <th style={{ width: '80px', padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {allRows.map(row => {
                  const eff = row.effectiveStatus || row.effective_status;
                  const isSelected = !!selected[row.recordId];
                  return (
                    <tr key={row.recordId} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: isSelected ? 'rgba(74,158,255,0.07)' : 'transparent' }}>
                      <td style={{ padding: '10px 12px' }}>
                        {eff !== 'PASS' && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onSelect(row.recordId, { personType: row.personType, personId: row.personId })}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '13px', color: '#e2e8f0', fontWeight: 600 }}>{row.name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: row.personType === 'EMPLOYEE' ? '#3b82f622' : '#a78bfa22', color: row.personType === 'EMPLOYEE' ? '#3b82f6' : '#a78bfa' }}>
                          {row.personType}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: (STATUS_COLOR[eff] || '#64748b') + '22', color: STATUS_COLOR[eff] || '#64748b' }}>
                          {eff}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#94a3b8' }}>
                        {row.completedAt ? new Date(row.completedAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: eff === 'EXPIRED' ? '#ef4444' : '#94a3b8' }}>
                        {row.expiryDate ? new Date(row.expiryDate).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {eff !== 'PASS' && (
                          <button
                            onClick={() => onMarkPassed(row, row.personType)}
                            style={{ background: '#4caf5022', border: '1px solid #4caf5055', color: '#4caf50', borderRadius: '5px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            Mark Passed
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AttendanceLogPanel({ course, records, loading, siteId, sites, onAdd, onDelete, onClose }) {
  const [showAddForm, setShowAddForm] = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#131a2b', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#e2e8f0' }}>{course.name} — Attendance Log</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{records.length} record{records.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowAddForm(v => !v)}
              style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: C.blue, color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >
              {showAddForm ? 'Cancel' : '+ Add Record'}
            </button>
            <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: '#64748b', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>

        {showAddForm && (
          <AddAttendanceForm
            siteId={siteId}
            sites={sites}
            onSave={async (data) => { await onAdd(data); setShowAddForm(false); }}
          />
        )}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading…</div>
          ) : records.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No attendance records yet. Click "+ Add Record" to log the first entry.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Name', 'Type', 'Site', 'Date', 'Result', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: '#e2e8f0', fontWeight: 600 }}>{r.personName}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: r.personType === 'EMPLOYEE' ? '#3b82f622' : '#a78bfa22', color: r.personType === 'EMPLOYEE' ? '#3b82f6' : '#a78bfa' }}>
                        {r.personType}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#94a3b8' }}>{r.siteName || '—'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '12px', color: '#94a3b8' }}>
                      {r.attendedAt ? new Date(r.attendedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: r.result === 'PASS' ? '#4caf5022' : '#ef444422', color: r.result === 'PASS' ? '#4caf50' : '#ef4444' }}>
                        {r.result}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => onDelete(r.id)}
                        style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '5px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AddAttendanceForm({ siteId, sites, onSave }) {
  const [selectedSiteId, setSelectedSiteId] = useState(siteId || (sites.length === 1 ? sites[0].id : ''));
  const [personType, setPersonType] = useState('EMPLOYEE');
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState('');
  const [attendedAt, setAttendedAt] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState('PASS');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedSiteId) { setPeople([]); return; }
    let active = true;
    setPeopleLoading(true);
    setSelectedPerson('');
    hseApi.getSitePeople(selectedSiteId)
      .then(data => { if (active) setPeople(data); })
      .finally(() => { if (active) setPeopleLoading(false); });
    return () => { active = false; };
  }, [selectedSiteId]);

  const filteredPeople = people.filter(p => p.personType === personType);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedPerson || !selectedSiteId) return;
    const person = people.find(p => p.id === selectedPerson);
    if (!person) return;
    setSaving(true);
    try {
      await onSave({
        siteId: selectedSiteId,
        personType: person.personType,
        personId: person.id,
        personName: person.name,
        attendedAt,
        result,
      });
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = { background: '#1e2a3a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '7px 10px', color: '#e2e8f0', fontSize: '13px' };

  return (
    <form
      onSubmit={handleSubmit}
      style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}
    >
      {!siteId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Site</label>
          <select value={selectedSiteId} onChange={e => setSelectedSiteId(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} required>
            <option value="">Select site…</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</label>
        <div style={{ display: 'flex' }}>
          {['EMPLOYEE', 'VISITOR'].map((t, i) => (
            <button
              key={t}
              type="button"
              onClick={() => { setPersonType(t); setSelectedPerson(''); }}
              style={{ padding: '7px 12px', border: '1px solid rgba(255,255,255,0.1)', background: personType === t ? C.blue : '#1e2a3a', color: personType === t ? '#fff' : '#94a3b8', fontSize: '12px', cursor: 'pointer', borderRadius: i === 0 ? '6px 0 0 6px' : '0 6px 6px 0' }}
            >
              {t === 'EMPLOYEE' ? 'Employee' : 'Visitor'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '160px' }}>
        <label style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Person</label>
        <select value={selectedPerson} onChange={e => setSelectedPerson(e.target.value)} style={{ ...inputStyle }} required disabled={!selectedSiteId || peopleLoading}>
          <option value="">{peopleLoading ? 'Loading…' : filteredPeople.length === 0 ? `No ${personType.toLowerCase()}s at this site` : 'Select person…'}</option>
          {filteredPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</label>
        <input type="date" value={attendedAt} onChange={e => setAttendedAt(e.target.value)} style={{ ...inputStyle }} required />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Result</label>
        <div style={{ display: 'flex' }}>
          {['PASS', 'FAIL'].map((r, i) => (
            <button
              key={r}
              type="button"
              onClick={() => setResult(r)}
              style={{ padding: '7px 14px', border: '1px solid rgba(255,255,255,0.1)', background: result === r ? (r === 'PASS' ? '#4caf50' : '#ef4444') : '#1e2a3a', color: result === r ? '#fff' : '#94a3b8', fontSize: '12px', fontWeight: result === r ? 700 : 400, cursor: 'pointer', borderRadius: i === 0 ? '6px 0 0 6px' : '0 6px 6px 0' }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={saving || !selectedPerson || !selectedSiteId}
        style={{ padding: '7px 16px', borderRadius: '6px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: (saving || !selectedPerson || !selectedSiteId) ? 0.5 : 1, alignSelf: 'flex-end' }}
      >
        {saving ? 'Adding…' : 'Add'}
      </button>
    </form>
  );
}

function HseProfilerQrModal({ onClose }) {
  const url = window.location.origin + '/?hse=1';
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '28px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', maxWidth: '320px', width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary, marginBottom: '4px' }}>HSE Profiler</div>
          <div style={{ fontSize: '12px', color: C.textMuted }}>Print and post this QR at your site. Officers scan it with their phone to open the profiler.</div>
        </div>
        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px' }}>
          <RealQRCode value={url} size={200} darkColor="#0a0e17" lightColor="#ffffff" />
        </div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 14px', fontSize: '12px', color: C.blue, fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
          {url}
        </div>
        <button
          onClick={onClose}
          style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSecondary, borderRadius: '7px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer' }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
